import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  Grid,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
  Paper,
  FormControl,
  Select,
  Divider,
  Button,
  Tooltip,
  CircularProgress,
  InputBase,
  useTheme,
  alpha,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Refresh as RefreshIcon,
  Public as RegionIcon,
  Storage as StorageIcon,
  Business as BusinessIcon,
  ShowChart as ChartIcon,
  GetApp as DownloadIcon,
  Info as InfoIcon,
  ArrowBack as ArrowBackIcon,
  DateRange as DateRangeIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  Close as CloseIcon,
  DesktopWindows as DeviceIcon,
} from '@mui/icons-material';
import { useAppData } from '../contexts/AppDataContext';
import { Pie } from 'react-chartjs-2';
import { getFullClientName, isExcludedInternalClient } from '../utils/clientNameMappings';
import DateRangePicker from '../components/DateRangePicker';

// Helper function to get colors
function getColor(index, alpha = 1) {
  const colors = [
    `rgba(58, 134, 255, ${alpha})`,   // blue - #3a86ff (primary)
    `rgba(76, 201, 189, ${alpha})`,   // teal - #4cc9bd (secondary)
    `rgba(255, 183, 77, ${alpha})`,   // amber - #ffb74d (tertiary)
    `rgba(255, 153, 102, ${alpha})`,  // soft orange - #ff9966 (quaternary)
    `rgba(76, 175, 80, ${alpha})`,    // green
    `rgba(66, 165, 245, ${alpha})`,   // light blue
    `rgba(126, 87, 194, ${alpha})`,   // purple
    `rgba(240, 98, 146, ${alpha})`,   // pink
    `rgba(103, 58, 183, ${alpha})`,   // deep purple
    `rgba(0, 150, 136, ${alpha})`,    // teal
  ];
  return colors[index % colors.length];
}

export default function Products() {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  
  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [region, setRegion] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [availableDeviceIds, setAvailableDeviceIds] = useState([]);
  const [loadingDeviceIds, setLoadingDeviceIds] = useState(false);
  const [expandedProduct, setExpandedProduct] = useState(null);
  // Create state for date filter with today's date as default
  const today = useMemo(() => new Date().toISOString().split('T')[0], []);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [dateRange, setDateRange] = useState({
    from: new Date(today),
    to: new Date(today)
  });
  // State for loading date filter
  const [loadingDateFilter, setLoadingDateFilter] = useState(false);
  // Add state for search type
  const [searchType, setSearchType] = useState('product'); // 'product' or 'device_id'
  
  // Get data from context
  const {
    clientLogs,
    regions,
    loading,
    fetchRegions,
    fetchClientLogs,
    refreshData,
    api
  } = useAppData();
  
  // Parse query params from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const regionParam = params.get('region');
    const fromDateParam = params.get('from_date');
    const toDateParam = params.get('to_date');
    const deviceIdParam = params.get('device_id');
    
    if (regionParam) {
      setRegion(regionParam);
    }
    
    if (fromDateParam) {
      setFromDate(fromDateParam);
      
      // Update the date range object
      if (toDateParam) {
        setDateRange({
          from: new Date(fromDateParam),
          to: new Date(toDateParam)
        });
      }
    }
    
    if (toDateParam) {
      setToDate(toDateParam);
    }

    if (deviceIdParam) {
      setDeviceId(deviceIdParam);
      setSearchTerm(deviceIdParam);
      setSearchType('device_id');
    }
  }, [location.search]);

  // Fetch available device IDs when region changes
  useEffect(() => {
    const fetchDeviceIds = async () => {
      try {
        setLoadingDeviceIds(true);
        const response = await api.get(`/api/device_ids${region ? `?region=${region}` : ''}`);
        if (response.data && response.data.device_ids) {
          setAvailableDeviceIds(response.data.device_ids);
        }
      } catch (error) {
        console.error('Error fetching device IDs:', error);
      } finally {
        setLoadingDeviceIds(false);
      }
    };

    fetchDeviceIds();
  }, [region, api]);
  
  // Load data when region, date range, or device ID changes
  useEffect(() => {
    let isMounted = true;
    const loadTimer = setTimeout(() => {
      const loadData = async () => {
        try {
          if (fromDate && toDate) {
            setLoadingDateFilter(true);
          }
          await Promise.all([
            fetchRegions(),
            fetchClientLogs({ 
              region, 
              from_date: fromDate, 
              to_date: toDate,
              device_id: deviceId 
            })
          ]);
          if (fromDate && toDate) {
            setLoadingDateFilter(false);
          }
        } catch (error) {
          console.error('Error loading product data:', error);
          setLoadingDateFilter(false);
        }
      };
      
      if (isMounted) {
        loadData();
      }
    }, 300); // Add a small delay to prevent excessive API calls during rapid filter changes
    
    return () => {
      isMounted = false;
      clearTimeout(loadTimer);
    };
  }, [region, deviceId, fetchRegions, fetchClientLogs, fromDate, toDate]);
  
  // Handle refresh data
  const handleRefresh = useCallback(async () => {
    try {
      await fetchClientLogs({ 
        region, 
        from_date: fromDate, 
        to_date: toDate,
        device_id: deviceId,
        forceRefresh: true
      });
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  }, [fetchClientLogs, region, fromDate, toDate, deviceId]);
  
  // Handle region change
  const handleRegionChange = useCallback((event) => {
    const newRegion = event.target.value;
    setRegion(newRegion);
    
    // Build URL with all parameters
    const params = new URLSearchParams();
    if (newRegion) params.set('region', newRegion);
    if (fromDate) params.set('from_date', fromDate);
    if (toDate) params.set('to_date', toDate);
    if (deviceId) params.set('device_id', deviceId);
    
    navigate(`/products${params.toString() ? `?${params.toString()}` : ''}`);
  }, [navigate, fromDate, toDate, deviceId]);

  // Handle device ID change
  const handleDeviceIdChange = useCallback((event) => {
    const newDeviceId = event.target.value;
    setDeviceId(newDeviceId);
    
    // Build URL with all parameters
    const params = new URLSearchParams();
    if (region) params.set('region', region);
    if (fromDate) params.set('from_date', fromDate);
    if (toDate) params.set('to_date', toDate);
    if (newDeviceId) params.set('device_id', newDeviceId);
    
    navigate(`/products${params.toString() ? `?${params.toString()}` : ''}`);
  }, [navigate, region, fromDate, toDate]);
  
  // Update clear filter handler to reset to today's date
  const handleClearDateFilter = useCallback(() => {
    // Get today's date in YYYY-MM-DD format
    const today = new Date().toISOString().split('T')[0];
    
    // Reset to today's date instead of empty strings
    setFromDate(today);
    setToDate(today);
    setDateRange({
      from: new Date(today),
      to: new Date(today)
    });
    setLoadingDateFilter(true);
    
    // Fetch client logs with today's date
    fetchClientLogs({ region, from_date: today, to_date: today, device_id: deviceId })
      .then(() => {
        setLoadingDateFilter(false);
        
        // Update URL with today's date
        const params = new URLSearchParams(location.search);
        params.set('from_date', today);
        params.set('to_date', today);
        navigate(`${location.pathname}?${params.toString()}`);
      })
      .catch(error => {
        console.error('Error resetting date filter:', error);
        setLoadingDateFilter(false);
      });
  }, [region, deviceId, fetchClientLogs, location.search, location.pathname, navigate, setDateRange]);

  // Handle date range change from the DateRangePicker
  const handleDateRangeChange = useCallback((newDateRange) => {
    if (!newDateRange?.from || !newDateRange?.to) return;
    
    const fromDateStr = newDateRange.from.toISOString().split('T')[0];
    const toDateStr = newDateRange.to.toISOString().split('T')[0];
    
    // Update state first
    setDateRange(newDateRange);
    setFromDate(fromDateStr);
    setToDate(toDateStr);
    
    // Set loading state
    setLoadingDateFilter(true);
    
    // Apply the date filter
    // Use a small timeout to ensure state updates are processed
    setTimeout(() => {
      // Apply the date filter directly with the values we have
      fetchClientLogs({ 
        region, 
        from_date: fromDateStr, 
        to_date: toDateStr, 
        device_id: deviceId,
        forceRefresh: true
      })
      .then(() => setLoadingDateFilter(false))
      .catch(error => {
        console.error('Error applying date filter:', error);
        setLoadingDateFilter(false);
      });
      
      // Build URL with all parameters
      const params = new URLSearchParams(location.search);
      params.set('from_date', fromDateStr);
      params.set('to_date', toDateStr);
      if (region) params.set('region', region);
      if (deviceId) params.set('device_id', deviceId);
      
      // Update URL - this does NOT trigger a navigation
      window.history.replaceState(
        {}, 
        '', 
        `${location.pathname}?${params.toString()}`
      );
    }, 50);
  }, [fetchClientLogs, region, deviceId, location.pathname, location.search]);

  // Clear all filters
  const handleClearAllFilters = useCallback(() => {
    const today = new Date().toISOString().split('T')[0];
    const todayObj = new Date();
    
    setRegion('');
    setFromDate(today);
    setToDate(today);
    setDateRange({
      from: todayObj,
      to: todayObj
    });
    setDeviceId('');
    setSearchTerm('');
    setSearchType('product');
    
    // Fetch client logs with cleared filters
    fetchClientLogs({ 
      from_date: today, 
      to_date: today,
      forceRefresh: true
    });
    
    // Update URL to remove all params
    navigate('/products');
  }, [navigate, fetchClientLogs]);
  
  // Handle search
  const handleSearchChange = useCallback((event) => {
    const value = event.target.value;
    setSearchTerm(value);
    
    // If in device ID mode, update the device ID
    if (searchType === 'device_id') {
      setDeviceId(value);
      
      // Update URL with device ID and preserve other params
      const params = new URLSearchParams(location.search);
      if (value) {
        params.set('device_id', value);
      } else {
        params.delete('device_id');
      }
      
      // Preserve other important parameters
      if (region) params.set('region', region);
      if (fromDate) params.set('from_date', fromDate);
      if (toDate) params.set('to_date', toDate);
      
      navigate(`${location.pathname}?${params.toString()}`);
    } else {
      // If in product search mode, clear the device ID
      if (deviceId) {
        setDeviceId('');
        const params = new URLSearchParams(location.search);
        params.delete('device_id');
        
        // Preserve other important parameters
        if (region) params.set('region', region);
        if (fromDate) params.set('from_date', fromDate);
        if (toDate) params.set('to_date', toDate);
        
        navigate(`${location.pathname}?${params.toString()}`);
      }
    }
  }, [searchType, location.search, location.pathname, navigate, deviceId, region, fromDate, toDate]);
  
  // Toggle search type between product name and device ID
  const toggleSearchType = useCallback(() => {
    // Clear search term when changing modes
    setSearchTerm('');
    if (searchType === 'product') {
      setSearchType('device_id');
    } else {
      setSearchType('product');
      // Clear device ID when switching to product search
      if (deviceId) {
        setDeviceId('');
        const params = new URLSearchParams(location.search);
        params.delete('device_id');
        
        // Preserve other important parameters
        if (region) params.set('region', region);
        if (fromDate) params.set('from_date', fromDate);
        if (toDate) params.set('to_date', toDate);
        
        navigate(`${location.pathname}?${params.toString()}`);
      }
    }
  }, [searchType, deviceId, navigate, location.pathname, region, fromDate, toDate]);
  
  // Navigate back to dashboard
  const handleBackToDashboard = useCallback(() => {
    navigate(region ? `/?region=${region}` : '/');
  }, [navigate, region]);

  // ENHANCEMENT: Function to enrich clientLogs with region information
  const enrichClientLogsWithRegions = useCallback((clientLogsData, regionsData) => {
    if (!clientLogsData || !regionsData || !clientLogsData.by_product) {
      return clientLogsData;
    }

    console.log('Enriching clientLogs with regions:', {
      regionsData,
      currentRegionFilter: region,
      sampleItems: clientLogsData.by_product.slice(0, 3)
    });
    
    // Debug: Log what regions data we have
    console.log('Available regions from API:', regionsData);
    console.log('Current region filter:', region);
    console.log('Sample items before enrichment:', clientLogsData.by_product.slice(0, 3).map(item => ({
      product: item.product,
      client: item.client_name,
      hasRegion: !!item.region,
      regionValue: item.region
    })));

    // IMPROVED: Use backend-provided region information when available
    const enrichedByProduct = clientLogsData.by_product.map(item => {
      // Debug: Log original region data
      console.log(`Processing item: ${item.product} - ${item.client_name}`, {
        originalRegion: item.region,
        originalRegionType: typeof item.region,
        isArray: Array.isArray(item.region)
      });
      
      let itemRegions = [];
      
      // Strategy 1: Use backend-provided region information (preferred)
      if (item.region && (Array.isArray(item.region) ? item.region.length > 0 : item.region)) {
        if (Array.isArray(item.region)) {
          itemRegions.push(...item.region);
        } else {
          itemRegions.push(item.region);
        }
        console.log(`Item ${item.product} for client ${item.client_name} using backend region: ${itemRegions.join(', ')}`);
      } else if (region) {
        // Strategy 2: If backend didn't provide region info but we have a filter, use the filter
        itemRegions.push(region);
        console.log(`Item ${item.product} for client ${item.client_name} assigned filter region: ${region}`);
      } else {
        // Strategy 3: If no region info at all, mark as Global
        itemRegions.push('Global');
        console.log(`Item ${item.product} for client ${item.client_name} assigned Global region (no info available)`);
      }
      
      // Debug: Log what region was assigned and why
      console.log(`Final region assignment for ${item.product} - ${item.client_name}:`, {
        assignedRegions: itemRegions,
        reason: item.region ? 'Backend provided' : 
                region ? 'Filter region' : 'Global (no info)'
      });

      return {
        ...item,
        region: itemRegions
      };
    });

    console.log('Enrichment complete. Sample enriched items:', enrichedByProduct.slice(0, 3));

    return {
      ...clientLogsData,
      by_product: enrichedByProduct
    };
  }, [region]);
  
  // Prepare product data - memoized
  const productData = useMemo(() => {
    if (!clientLogs || !clientLogs.by_product) return [];
    
    // ENHANCEMENT: Use enriched clientLogs data with region information
    console.log('Regions data before enrichment:', regions);
    console.log('Regions data type:', typeof regions);
    console.log('Regions data length:', regions ? regions.length : 'undefined');
    console.log('Current URL:', window.location.href);
    console.log('Current region filter state:', region);
    console.log('URL search params:', new URLSearchParams(window.location.search).toString());
    
    const enrichedClientLogs = enrichClientLogsWithRegions(clientLogs, regions);
    const byProductRows = enrichedClientLogs.by_product.filter(
      (item) => !isExcludedInternalClient(item.client_name)
    );

    // Debug: Log the raw data structure
    console.log('Raw clientLogs.by_product data:', byProductRows.slice(0, 5));
    console.log('Sample enriched items with regions:', byProductRows.slice(0, 3).map(item => ({
      product: item.product,
      client: item.client_name,
      region: item.region,
      regionType: typeof item.region,
      isArray: Array.isArray(item.region)
    })));
    
    // Debug: Check if there's a pattern in the data
    const uniqueRegions = new Set();
    byProductRows.forEach(item => {
      if (item.region && Array.isArray(item.region)) {
        item.region.forEach(region => uniqueRegions.add(region));
      }
    });
    console.log('Unique regions found in enriched data:', Array.from(uniqueRegions));
    console.log('Total items processed:', byProductRows.length);
    
    // Get all unique products
    const uniqueProducts = new Map();
    
    byProductRows.forEach(item => {
      const key = item.product;
      
      if (!uniqueProducts.has(key)) {
        uniqueProducts.set(key, {
          product: item.product,
          totalSize: 0,
          clients: new Set(),
          regions: new Set(),
          items: []
        });
      }
      
      const productEntry = uniqueProducts.get(key);
      productEntry.totalSize += item.size_gb;
      productEntry.clients.add(item.client_name);
      
      // Enhanced region detection using enriched data from backend
      if (item.region) {
        if (Array.isArray(item.region)) {
          item.region.forEach(region => {
            if (region && typeof region === 'string' && region.trim() !== '' && 
                region.toLowerCase() !== 'global' && region.toLowerCase() !== 'na' && 
                region.toLowerCase() !== 'null' && region.toLowerCase() !== 'undefined') {
              productEntry.regions.add(region);
            }
          });
        } else if (typeof item.region === 'string' && item.region.trim() !== '' && 
                   item.region.toLowerCase() !== 'global' && item.region.toLowerCase() !== 'na' && 
                   item.region.toLowerCase() !== 'null' && item.region.toLowerCase() !== 'undefined') {
          productEntry.regions.add(item.region);
        }
      }
      productEntry.items.push(item);
    });
    
    // Convert to array and add computed properties
    const result = Array.from(uniqueProducts.values()).map(product => {
      // Determine if product is truly global or region-specific
      const hasExplicitRegions = product.regions.size > 0;
      const isGlobal = !hasExplicitRegions;
      
      // Debug logging for first few products
      if (product.product === 'Silverfort' || product.product === 'MSSQLServer') {
        console.log(`Product: ${product.product}`, {
          rawItems: product.items.map(item => ({ 
            region: item.region, 
            regionType: typeof item.region,
            regionTrimmed: Array.isArray(item.region) ? item.region.join(', ') : (item.region ? item.region.trim() : 'N/A'),
            client: item.client_name 
          })),
          regions: Array.from(product.regions),
          isGlobal,
          hasExplicitRegions
        });
      }
      
      return {
        ...product,
        clientCount: product.clients.size,
        regionCount: product.regions.size,
        regions: Array.from(product.regions),
        clients: Array.from(product.clients),
        isGlobal: isGlobal,
        // If no explicit regions, we'll show "Global" in the UI
        displayRegions: hasExplicitRegions ? Array.from(product.regions) : ['Global']
      };
    });
    
    return result;
  }, [clientLogs, regions, enrichClientLogsWithRegions]);
  
  // Filter products by region and search term
  const filteredProducts = useMemo(() => {
    if (!productData) return [];
    
    return productData.filter(product => {
      // Exclude default products
      if (
        product.product.toLowerCase() === 'rsyslog' ||
        product.product.toLowerCase() === 'other' ||
        product.product.toLowerCase() === 'error' ||
        product.product.toLowerCase() === 'default' ||
        product.product.toLowerCase() === 'na' ||
        product.product.toLowerCase() === 'unknown' ||
        product.product.toLowerCase() === 'unknownproduct' ||
        product.product === '' 
      ) {
        return false;
      }
      
      // Filter by region if selected
      if (region && !product.regions.includes(region) && product.regions.length > 0) {
        return false;
      }
      
      // Filter by search term based on search type
      if (searchType === 'product' && searchTerm) {
        if (!product.product.toLowerCase().includes(searchTerm.toLowerCase())) {
          return false;
        }
      }
      
      // Note: We don't filter by device ID here because that's handled by the API
      // when deviceId is passed in fetchClientLogs
      
      return true;
    });
  }, [productData, region, searchTerm, searchType]);
  
  // Prepare client distribution chart data for a product
  const getClientChartData = useCallback((productName) => {
    if (!clientLogs || !clientLogs.by_product) return null;
    
    let productItems = clientLogs.by_product.filter(
      item => item.product === productName && !isExcludedInternalClient(item.client_name)
    );
    
    // Filter by region if needed using backend-provided region information
    if (region) {
      productItems = productItems.filter(item => {
        if (!item.region) return true; // No region info, include it
        if (Array.isArray(item.region)) {
          return item.region.includes(region); // Check if region is in the array
        }
        return item.region === region; // Fallback for string regions
      });
    }
    
    // Group by client and sum sizes
    const clientSizes = new Map();
    productItems.forEach(item => {
      const client = item.client_name;
      if (!clientSizes.has(client)) {
        clientSizes.set(client, 0);
      }
      clientSizes.set(client, clientSizes.get(client) + item.size_gb);
    });
    
    // Sort by size and get top clients
    const sortedClients = Array.from(clientSizes.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5); // Top 5 clients
    
    // Create chart data
    return {
      labels: sortedClients.map(([client]) => client),
      datasets: [
        {
          data: sortedClients.map(([_, size]) => size),
          backgroundColor: sortedClients.map((_, i) => getColor(i, 0.7)),
          borderColor: sortedClients.map((_, i) => getColor(i)),
          borderWidth: 1,
        },
      ],
    };
  }, [clientLogs, region]);
  
  // Helper to get region display
  const getRegionDisplay = useCallback((regions) => {
    if (!regions || regions.length === 0) {
      return 'Global';
    }
    return regions;
  }, []);

  // Chart options
  const pieOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          boxWidth: 12,
          font: {
            size: 10,
          },
          color: theme.palette.text.secondary,
        },
      },
      tooltip: {
        backgroundColor: theme.palette.background.paper,
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: alpha(theme.palette.text.primary, 0.1),
        borderWidth: 1,
        padding: 10,
        boxPadding: 5,
      },
    },
    animation: false, // Disable animations for better performance
  }), [theme.palette]);
  
  // Toggle product expansion
  const handleProductToggle = useCallback((productName) => {
    setExpandedProduct(prev => prev === productName ? null : productName);
  }, []);
  
  // Add a function to handle CSV download
  const handleDownload = useCallback((productName) => {
    try {
      // Default to downloading all products if no specific product is provided
      const productsToDownload = productName 
        ? filteredProducts.filter(p => p.product === productName)
        : filteredProducts;
      
      if (productsToDownload.length === 0) {
        console.error('No product data to download');
        return;
      }
      
      // Function to escape CSV values properly
      const escapeCSV = (value) => {
        if (value === null || value === undefined) {
          return '';
        }
        
        const stringValue = String(value);
        // If the value contains commas, quotes, or newlines, escape it properly
        if (stringValue.includes('"') || stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('\r')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      };
      
      // Array to hold rows
      const rows = [];
      
      // Add header row
      rows.push(['Product Name', 'Regions', 'Client Count', 'Log Size (GB)', 'Date Range', 'Date Exported'].map(escapeCSV));
      
      // Get current date for export timestamp
      const exportDate = new Date().toISOString().slice(0, 10);
      
      // Format date range for CSV
      const dateRangeText = fromDate && toDate 
        ? `${fromDate} to ${toDate}`
        : 'All Time';
      
      // Add data rows
      productsToDownload.forEach(product => {
        rows.push([
          product.product,
          product.regions.join(', '),
          product.clientCount,
          product.totalSize.toFixed(2),
          dateRangeText,
          exportDate
        ].map(escapeCSV));
      });
      
      // Join rows into CSV content
      const csvContent = rows.map(row => row.join(',')).join('\n');
      
      // Create a blob with the CSV content
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // Create a URL for the blob
      const url = URL.createObjectURL(blob);
      
      // Create filename with date range info if available
      let filename = productName 
        ? `${productName}_logs` 
        : `products_logs`;
        
      // Add date range to filename if available
      if (fromDate && toDate) {
        filename += `_${fromDate}_to_${toDate}`;
      }
      
      filename += `_${exportDate}.csv`;
      
      // Create a download link
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      
      // Append the link to the document, click it, and remove it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Revoke the URL to free memory
      URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error generating CSV:', error);
    }
  }, [filteredProducts, fromDate, toDate]);
  
  // Update where client names are displayed
  const renderClientName = (clientName) => {
    return getFullClientName(clientName);
  };
  
  // Update chart data preparation
  const prepareChartData = (data) => {
    if (!data) return null;
    
    return {
      labels: data.map(item => `${item.product} (${getFullClientName(item.client_name)})`),
      datasets: [{
        label: "Log Size (GB)",
        data: data.map(item => item.size_gb),
        backgroundColor: Array(data.length).fill().map((_, i) => alpha(getColor(i), 0.7)),
        borderColor: Array(data.length).fill().map((_, i) => getColor(i)),
        borderWidth: 1
      }]
    };
  };
  
  return (
    <Box sx={{ pb: 4 }}>
      {/* Header with gradient background */}
      <Box
        sx={{
          background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
          borderRadius: '16px',
          mb: 3,
          p: 3,
          boxShadow: `0 8px 16px ${alpha(theme.palette.common.black, 0.05)}`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          }
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Box display="flex" alignItems="center" gap={1.5} mb={1}>
              <Button
                variant="contained"
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={handleBackToDashboard}
                sx={{
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.2),
                    boxShadow: 'none',
                  },
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 'medium',
                  px: 2,
                }}
              >
                Back to Dashboard
              </Button>
            </Box>
            <Typography 
              variant="h4" 
              fontWeight="bold" 
              gutterBottom
              sx={{
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.5px',
              }}
            >
              {region ? `Products in ${region} Region` : 'All Products'}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Monitor and manage product logs and client distribution
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <DateRangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                isLoading={loadingDateFilter}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box 
              sx={{ 
                display: 'flex', 
                gap: 1.5, 
                justifyContent: 'flex-end', 
                alignItems: 'center', 
                height: '100%',
                mt: { xs: 2, md: 0 }
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: '10px',
                  backgroundColor: alpha(theme.palette.background.paper, 0.8),
                  border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
                  pl: 1.5,
                  flexGrow: 1,
                  minWidth: '250px',
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                  },
                }}
              >
                {searchType === 'product' ? (
                  <SearchIcon sx={{ color: alpha(theme.palette.text.secondary, 0.7), fontSize: 18, mr: 1 }} />
                ) : (
                  <DeviceIcon sx={{ color: theme.palette.primary.main, fontSize: 18, mr: 1 }} />
                )}
                <InputBase
                  placeholder={searchType === 'product' ? "Search products..." : "Enter device ID..."}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  sx={{
                    flexGrow: 1,
                    width: '100%',
                    py: 1.2,
                    fontSize: '0.875rem',
                    color: theme.palette.text.primary,
                    '& ::placeholder': {
                      color: alpha(theme.palette.text.secondary, 0.6),
                      opacity: 1,
                    },
                  }}
                />
                <Tooltip title={searchType === 'product' ? "Switch to device ID search" : "Switch to product search"}>
                  <IconButton 
                    onClick={toggleSearchType}
                    size="small"
                    sx={{
                      mr: 1,
                      color: searchType === 'product' ? alpha(theme.palette.text.secondary, 0.7) : theme.palette.primary.main,
                      bgcolor: searchType === 'product' ? 'transparent' : alpha(theme.palette.primary.main, 0.1),
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                      }
                    }}
                  >
                    {searchType === 'product' ? <DeviceIcon fontSize="small" /> : <SearchIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Paper>
              
              <FormControl 
                variant="outlined" 
                size="small" 
                sx={{ 
                  minWidth: 150,
                }}
              >
                <Select
                  value={region}
                  onChange={handleRegionChange}
                  displayEmpty
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <RegionIcon fontSize="small" sx={{ color: alpha(theme.palette.text.secondary, 0.8) }} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {selected || 'All Regions'}
                      </Typography>
                    </Box>
                  )}
                  sx={{
                    borderRadius: '10px',
                    backgroundColor: alpha(theme.palette.background.paper, 0.8),
                    border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
                    height: '40px',
                    fontSize: '0.875rem',
                    transition: 'all 0.2s',
                    '&:hover': {
                      boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                    },
                    '&.Mui-focused': {
                      borderColor: theme.palette.primary.main,
                      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                    },
                    '& .MuiSelect-select': {
                      paddingRight: 3,
                      paddingLeft: 2,
                    },
                  }}
                  IconComponent={(props) => (
                    <Box 
                      component="div" 
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'absolute',
                        right: 7,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none',
                        color: alpha(theme.palette.text.primary, 0.5),
                      }}
                    >
                      <ArrowDropDownIcon {...props} />
                    </Box>
                  )}
                  MenuProps={{
                    PaperProps: {
                      elevation: 3,
                      sx: {
                        mt: 0.5,
                        borderRadius: '10px',
                        border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
                        boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.15)}`,
                        backgroundColor: theme.palette.background.paper,
                        maxHeight: 300,
                        '& .MuiMenuItem-root': {
                          fontSize: '0.875rem',
                          padding: '8px 16px',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.08),
                          },
                          '&.Mui-selected': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.12),
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.18),
                            },
                          }
                        }
                      },
                    },
                  }}
                >
                  <MenuItem value="">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <RegionIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                      <Typography>All Regions</Typography>
                    </Box>
                  </MenuItem>
                  {regions && regions.map((region) => (
                    <MenuItem key={region} value={region}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <RegionIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                        <Typography>{region}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Tooltip title="Download CSV">
                <IconButton 
                  onClick={() => handleDownload()}
                  sx={{
                    color: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    borderRadius: '10px',
                    width: '40px',
                    height: '40px',
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.2),
                    },
                  }}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Refresh Data">
                <IconButton 
                  onClick={handleRefresh}
                  sx={{
                    color: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    borderRadius: '10px',
                    width: '40px',
                    height: '40px',
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.2),
                      transform: 'rotate(30deg)',
                    },
                  }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Box>
      
      {/* Products Table */}
      <Card 
        elevation={0}
        sx={{ 
          borderRadius: '16px',
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
          position: 'relative',
        }}
      >
        {/* Table Header */}
        <Box 
          sx={{ 
            p: 2.5, 
            background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.03)} 100%)`,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="subtitle1" fontWeight="medium" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StorageIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
            {region ? `Products in ${region} Region` : 'All Products'} 
            <Chip 
              label={filteredProducts.length} 
              size="small"
              sx={{ 
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontWeight: 'bold',
                ml: 1,
                borderRadius: '6px',
                height: 22,
              }}
            />
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            {fromDate && toDate && (
              <Chip 
                icon={loadingDateFilter ? <CircularProgress size={16} /> : <DateRangeIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />}
                label={loadingDateFilter ? "Loading..." : `${fromDate} to ${toDate}`}
                size="small"
                onDelete={!loadingDateFilter ? handleClearDateFilter : undefined}
                sx={{ 
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main,
                  fontWeight: 'medium',
                  borderRadius: '10px',
                  px: 1,
                  py: 0.6,
                  height: 30,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  '& .MuiChip-label': {
                    px: 0.8,
                  },
                  '& .MuiChip-icon': {
                    ml: 0.5,
                  },
                  '& .MuiChip-deleteIcon': {
                    color: theme.palette.primary.main,
                    '&:hover': {
                      color: theme.palette.primary.dark,
                    },
                  },
                }}
              />
            )}
            {deviceId && (
              <Chip 
                icon={<DeviceIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />}
                label={deviceId}
                size="small"
                onDelete={() => {
                  setDeviceId('');
                  setSearchTerm('');
                  
                  // Update URL by removing device_id param
                  const params = new URLSearchParams(location.search);
                  params.delete('device_id');
                  navigate(`${location.pathname}?${params.toString()}`);
                }}
                sx={{ 
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main,
                  fontWeight: 'medium',
                  borderRadius: '10px',
                  px: 1,
                  py: 0.6,
                  height: 30,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  '& .MuiChip-label': {
                    px: 0.8,
                  },
                  '& .MuiChip-icon': {
                    ml: 0.5,
                  },
                  '& .MuiChip-deleteIcon': {
                    color: theme.palette.primary.main,
                    '&:hover': {
                      color: theme.palette.primary.dark,
                    },
                  },
                }}
              />
            )}
            <Chip 
              icon={<RegionIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />}
              label={region || 'All Regions'} 
              size="small"
              sx={{ 
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
                fontWeight: 'medium',
                borderRadius: '10px',
                px: 1,
                py: 0.6,
                height: 30,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                '& .MuiChip-label': {
                  px: 0.8,
                },
                '& .MuiChip-icon': {
                  ml: 0.5,
                },
              }}
            />
          </Box>
        </Box>
        
        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead sx={{ 
              backgroundColor: alpha(theme.palette.primary.main, 0.03),
              '& .MuiTableCell-head': {
                fontWeight: 'medium',
                color: theme.palette.text.secondary,
                py: 1.5,
              }
            }}>
              <TableRow>
                <TableCell>Product Name</TableCell>
                <TableCell>
                  <Tooltip title="Click on any region to filter products by that region">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                      Regions
                      <InfoIcon fontSize="small" sx={{ color: alpha(theme.palette.text.secondary, 0.6), fontSize: 14 }} />
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">Clients</TableCell>
                <TableCell align="right">Log Size (GB)</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProducts.map((product) => (
                <React.Fragment key={product.product}>
                  <TableRow 
                    hover
                    sx={{ 
                      '&:hover': { 
                        backgroundColor: alpha(theme.palette.primary.main, 0.04) 
                      },
                      '& td': { 
                        borderBottom: expandedProduct === product.product ? 
                          'none' : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        py: 1.75
                      },
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <IconButton
                          size="small"
                          onClick={() => handleProductToggle(product.product)}
                          sx={{
                            mr: 1,
                            transform: expandedProduct === product.product ? 'rotate(90deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s',
                            color: expandedProduct === product.product ? 
                              theme.palette.primary.main : theme.palette.text.secondary,
                            backgroundColor: expandedProduct === product.product ?
                              alpha(theme.palette.primary.main, 0.1) : 'transparent',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            }
                          }}
                        >
                          <ExpandMoreIcon />
                        </IconButton>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            color: expandedProduct === product.product ? 
                              theme.palette.primary.main : theme.palette.text.primary,
                            fontWeight: expandedProduct === product.product ? 'medium' : 'regular',
                          }}
                        >
                          {product.product}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                       <Box display="flex" flexDirection="column" gap={0.5}>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                           {product.displayRegions.map((regionName, idx) => (
                            <Chip 
                              key={idx} 
                              label={regionName} 
                              size="small"
                              sx={{ 
                                 backgroundColor: regionName === 'Global' 
                                   ? alpha(theme.palette.info.main, 0.1)
                                   : alpha(theme.palette.secondary.main, 0.1),
                                 color: regionName === 'Global' 
                                   ? theme.palette.info.main
                                   : theme.palette.secondary.main,
                                fontSize: '0.7rem',
                                height: '22px',
                                 cursor: regionName === 'Global' ? 'default' : 'pointer',
                                 position: 'relative',
                                 '&:hover': regionName === 'Global' ? {} : {
                                   backgroundColor: alpha(theme.palette.secondary.main, 0.2),
                                   boxShadow: `0 2px 4px ${alpha(theme.palette.secondary.main, 0.2)}`,
                                   transform: 'translateY(-1px)',
                                 },
                                 transition: 'all 0.2s ease',
                                 '&::after': regionName === 'Global' ? {} : {
                                   content: '"↗"',
                                   position: 'absolute',
                                   top: '-2px',
                                   right: '-2px',
                                   fontSize: '0.6rem',
                                   color: theme.palette.secondary.main,
                                   opacity: 0.7,
                                 },
                               }}
                               onClick={regionName === 'Global' ? undefined : () => {
                                 // Navigate to products page with region filter
                                 const params = new URLSearchParams();
                                 params.set('region', regionName);
                                 if (fromDate) params.set('from_date', fromDate);
                                 if (toDate) params.set('to_date', toDate);
                                 navigate(`/products?${params.toString()}`);
                               }}
                             />
                           ))}
                         </Box>
                         {!product.isGlobal && (
                           <Typography 
                             variant="caption" 
                             color="text.secondary"
                            sx={{ 
                               fontSize: '0.65rem',
                               fontStyle: 'italic',
                               mt: 0.5
                             }}
                           >
                             {product.regions.length === 1 ? '1 region' : `${product.regions.length} regions`}
                           </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        size="small" 
                        label={product.clientCount}
                        sx={{ 
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          fontWeight: 'medium',
                          borderRadius: '8px',
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        fontWeight="medium"
                        sx={{
                          color: theme.palette.secondary.main,
                          background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                        }}
                      >
                        {product.totalSize.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" justifyContent="flex-end" gap={1}>
                        <Tooltip title="Download CSV">
                      <IconButton 
                            onClick={() => handleDownload(product.product)}
                        size="small" 
                        sx={{ 
                          color: theme.palette.primary.main,
                          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
                          '&:hover': {
                            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
                          },
                          width: 32,
                          height: 32,
                          boxShadow: `0 2px 6px ${alpha(theme.palette.primary.main, 0.15)}`,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        }}
                      >
                            <DownloadIcon fontSize="small" />
                      </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                  
                  {/* Expanded Details Row */}
                  <TableRow 
                    sx={{ 
                      display: expandedProduct === product.product ? 'table-row' : 'none',
                      background: `linear-gradient(to right, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.primary.main, 0.01)})`,
                    }}
                  >
                    <TableCell colSpan={5} sx={{ p: 3 }}>
                      <Box sx={{ pb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <Typography 
                            variant="h6" 
                            fontWeight="bold"
                          sx={{ 
                            color: theme.palette.primary.main,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <StorageIcon fontSize="small" />
                            {product.product}
                          </Typography>
                          <Chip
                            label="Expanded View"
                            size="small"
                            sx={{
                              backgroundColor: alpha(theme.palette.success.main, 0.1),
                              color: theme.palette.success.main,
                              fontWeight: 'medium',
                              fontSize: '0.7rem',
                              height: '24px',
                            }}
                          />
                        </Box>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ 
                            mb: 2,
                            fontStyle: 'italic'
                          }}
                        >
                          Detailed breakdown of regions, clients, and log distribution
                        </Typography>
                        <Divider sx={{ 
                          mt: 1, 
                          mb: 2,
                          background: `linear-gradient(to right, ${alpha(theme.palette.primary.main, 0.2)}, transparent)`,
                          height: '2px',
                          border: 'none',
                        }} />
                        
                                                 {/* Product Summary Section */}
                         <Box 
                           sx={{ 
                             mb: 3, 
                             p: 2, 
                             borderRadius: '12px',
                             background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.secondary.main, 0.03)})`,
                             border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                           }}
                         >
                           <Grid container spacing={2} alignItems="center">
                             <Grid item xs={12} md={3}>
                               <Box sx={{ textAlign: 'center' }}>
                                 <Typography variant="h6" fontWeight="bold" color="primary.main">
                                   {product.isGlobal ? 'Global' : product.regions.length}
                                 </Typography>
                                 <Typography variant="caption" color="text.secondary">
                                   {product.isGlobal ? 'Global' : (product.regions.length === 1 ? 'Region' : 'Regions')}
                                 </Typography>
                               </Box>
                             </Grid>
                             <Grid item xs={12} md={3}>
                               <Box sx={{ textAlign: 'center' }}>
                                 <Typography variant="h6" fontWeight="bold" color="secondary.main">
                                   {product.clientCount}
                                 </Typography>
                                 <Typography variant="caption" color="text.secondary">
                                   {product.clientCount === 1 ? 'Client' : 'Clients'}
                                 </Typography>
                               </Box>
                             </Grid>
                             <Grid item xs={12} md={3}>
                               <Box sx={{ textAlign: 'center' }}>
                                 <Typography variant="h6" fontWeight="bold" color="info.main">
                                   {product.totalSize.toFixed(2)}
                                 </Typography>
                                 <Typography variant="caption" color="text.secondary">
                                   GB Logs
                                 </Typography>
                               </Box>
                             </Grid>
                             <Grid item xs={12} md={3}>
                               <Box sx={{ textAlign: 'center' }}>
                                 <Typography variant="h6" fontWeight="bold" color="success.main">
                                   {product.isGlobal ? 'Global' : 'Multi-Region'}
                                 </Typography>
                                 <Typography variant="caption" color="text.secondary">
                                   Availability
                                 </Typography>
                               </Box>
                             </Grid>
                           </Grid>
                         </Box>
                        
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}>
                            <Box 
                              sx={{ 
                                p: 2, 
                                borderRadius: '16px',
                                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                backgroundColor: theme.palette.background.paper,
                                height: '100%',
                                boxShadow: `0 4px 14px ${alpha(theme.palette.common.black, 0.04)}`,
                                position: 'relative',
                                overflow: 'hidden',
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  height: '3px',
                                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                }
                              }}
                            >
                              <Typography 
                                variant="subtitle2" 
                                gutterBottom
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                <ChartIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                                Top Clients by Log Size
                              </Typography>
                              <Box 
                                sx={{ 
                                  height: 200,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                {getClientChartData(product.product) && getClientChartData(product.product).labels.length > 0 ? (
                                  <Pie
                                    data={getClientChartData(product.product)}
                                    options={pieOptions}
                                  />
                                ) : (
                                  <Box sx={{ textAlign: 'center', p: 2 }}>
                                    <InfoIcon sx={{ color: alpha(theme.palette.text.secondary, 0.5), fontSize: 36, mb: 1 }} />
                                    <Typography color="text.secondary">No client data available</Typography>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </Grid>
                          
                          <Grid item xs={12} md={4}>
                            <Box 
                              sx={{ 
                                p: 2, 
                                borderRadius: '16px',
                                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                backgroundColor: theme.palette.background.paper,
                                height: '100%',
                                boxShadow: `0 4px 14px ${alpha(theme.palette.common.black, 0.04)}`,
                                position: 'relative',
                                overflow: 'hidden',
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  height: '3px',
                                  background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.info.main})`,
                                }
                              }}
                            >
                                                             <Typography 
                                 variant="subtitle2" 
                                 gutterBottom
                                 sx={{
                                   display: 'flex',
                                   alignItems: 'center',
                                   gap: 1,
                                 }}
                               >
                                 <RegionIcon fontSize="small" sx={{ color: theme.palette.secondary.main }} />
                                 Available Regions 
                                 <Chip 
                                   label={product.isGlobal ? 'Global' : product.regions.length} 
                                   size="small"
                                   sx={{ 
                                     backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                                     color: theme.palette.secondary.main,
                                     fontWeight: 'bold',
                                     borderRadius: '6px',
                                     height: 20,
                                     fontSize: '0.7rem',
                                   }}
                                 />
                               </Typography>
                                                             <Box sx={{ mt: 1 }}>
                                 {!product.isGlobal ? (
                                   <Grid container spacing={1}>
                                     {product.regions.map((regionName, idx) => {
                                       // Calculate client count and log size for this specific region
                                       const regionItems = product.items.filter(item => {
                                         if (!item.region) return false;
                                         if (Array.isArray(item.region)) {
                                           return item.region.includes(regionName);
                                         }
                                         return item.region === regionName;
                                       });
                                       const regionClientCount = regionItems.reduce((acc, item) => {
                                         if (!acc.has(item.client_name)) {
                                           acc.add(item.client_name);
                                         }
                                         return acc;
                                       }, new Set()).size;
                                       const regionLogSize = regionItems.reduce((sum, item) => sum + item.size_gb, 0);
                                       
                                       return (
                                         <Grid item key={idx} xs={12}>
                                           <Box
                                             sx={{
                                               p: 1.5,
                                               borderRadius: '10px',
                                               backgroundColor: alpha(getColor(idx + 2), 0.05),
                                               border: `1px solid ${alpha(getColor(idx + 2), 0.2)}`,
                                               cursor: 'pointer',
                                               transition: 'all 0.2s ease',
                                               '&:hover': {
                                                 backgroundColor: alpha(getColor(idx + 2), 0.1),
                                                 boxShadow: `0 2px 8px ${alpha(getColor(idx + 2), 0.15)}`,
                                                 transform: 'translateY(-1px)',
                                               },
                                             }}
                                             onClick={() => {
                                               // Navigate to products page with region filter
                                               const params = new URLSearchParams();
                                               params.set('region', regionName);
                                               if (fromDate) params.set('from_date', fromDate);
                                               if (toDate) params.set('to_date', toDate);
                                               navigate(`/products?${params.toString()}`);
                                             }}
                                           >
                                             <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                 <RegionIcon fontSize="small" sx={{ color: getColor(idx + 2) }} />
                                                 <Typography 
                                                   variant="body2" 
                                                   fontWeight="medium"
                                                   sx={{ color: getColor(idx + 2) }}
                                                 >
                                                   {regionName}
                                                 </Typography>
                                               </Box>
                                               <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                 <Chip
                                                   label={`${regionClientCount} clients`}
                                                   size="small"
                                                   sx={{
                                                     backgroundColor: alpha(getColor(idx + 2), 0.2),
                                                     color: getColor(idx + 2),
                                                     fontWeight: 'bold',
                                                     fontSize: '0.7rem',
                                                     height: '20px',
                                                     minWidth: 'auto',
                                                   }}
                                                 />
                                                 <Chip
                                                   label={`${regionLogSize.toFixed(2)} GB`}
                                                   size="small"
                                                   sx={{
                                                     backgroundColor: alpha(getColor(idx + 3), 0.2),
                                                     color: getColor(idx + 3),
                                                     fontWeight: 'bold',
                                                     fontSize: '0.7rem',
                                                     height: '20px',
                                                     minWidth: 'auto',
                                                   }}
                                                 />
                                               </Box>
                                             </Box>
                                           </Box>
                                         </Grid>
                                       );
                                     })}
                                   </Grid>
                                 ) : (
                                  <Box sx={{ textAlign: 'center', p: 2 }}>
                                    <Chip
                                      icon={<RegionIcon fontSize="small" />}
                                      label="Global Availability"
                                      size="medium"
                                      sx={{
                                        backgroundColor: alpha(theme.palette.info.main, 0.1),
                                        color: theme.palette.info.main,
                                        borderRadius: '10px',
                                        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                                        width: '100%',
                                        justifyContent: 'flex-start',
                                        '& .MuiChip-label': {
                                          fontSize: '0.875rem',
                                          fontWeight: 'medium',
                                        },
                                      }}
                                    />
                                    <Typography 
                                      variant="caption" 
                                      color="text.secondary" 
                                      sx={{ 
                                        display: 'block', 
                                        mt: 1,
                                        fontStyle: 'italic'
                                      }}
                                    >
                                      This product is available across all regions
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </Grid>
                          
                          <Grid item xs={12} md={4}>
                            <Box 
                              sx={{ 
                                p: 2, 
                                borderRadius: '16px',
                                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                backgroundColor: theme.palette.background.paper,
                                height: '100%',
                                boxShadow: `0 4px 14px ${alpha(theme.palette.common.black, 0.04)}`,
                                position: 'relative',
                                overflow: 'hidden',
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  height: '3px',
                                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                }
                              }}
                            >
                              <Typography 
                                variant="subtitle2" 
                                gutterBottom
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                <BusinessIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                                Clients Using This Product 
                                <Chip 
                                  label={product.clients.length} 
                                  size="small"
                                  sx={{ 
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    color: theme.palette.primary.main,
                                    fontWeight: 'bold',
                                    borderRadius: '6px',
                                    height: 20,
                                    fontSize: '0.7rem',
                                  }}
                                />
                              </Typography>
                              <Box sx={{ mt: 1 }}>
                                <Grid container spacing={1}>
                                  {product.clients.map((client, idx) => {
                                    // Get regions for this specific client
                                    const clientItems = product.items.filter(item => item.client_name === client);
                                    const clientRegions = new Set();
                                    
                                    clientItems.forEach(item => {
                                      if (item.region) {
                                        if (Array.isArray(item.region)) {
                                          item.region.forEach(region => clientRegions.add(region));
                                        } else if (typeof item.region === 'string') {
                                          clientRegions.add(item.region);
                                        }
                                      }
                                    });
                                    
                                    const regionsList = Array.from(clientRegions);
                                    
                                    return (
                                      <Grid item key={idx} xs={12}>
                                        <Box
                                          sx={{
                                            p: 1.5,
                                            borderRadius: '10px',
                                            backgroundColor: alpha(getColor(idx), 0.05),
                                            border: `1px solid ${alpha(getColor(idx), 0.2)}`,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                              backgroundColor: alpha(getColor(idx), 0.1),
                                              boxShadow: `0 2px 8px ${alpha(getColor(idx), 0.15)}`,
                                              transform: 'translateY(-1px)',
                                            },
                                          }}
                                          onClick={() => navigate(`/clients?search=${client}`)}
                                        >
                                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <BusinessIcon fontSize="small" sx={{ color: getColor(idx) }} />
                                              <Typography 
                                                variant="body2" 
                                                fontWeight="medium"
                                                sx={{ color: getColor(idx) }}
                                              >
                                                {renderClientName(client)}
                                              </Typography>
                                            </Box>
                                            <Chip
                                              label={`${regionsList.length} region${regionsList.length !== 1 ? 's' : ''}`}
                                              size="small"
                                              sx={{
                                                backgroundColor: alpha(getColor(idx), 0.2),
                                                color: getColor(idx),
                                                fontWeight: 'bold',
                                                fontSize: '0.7rem',
                                                height: '20px',
                                                minWidth: 'auto',
                                              }}
                                            />
                                          </Box>
                                          
                                          {/* Show regions for this client */}
                                          {regionsList.length > 0 && (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                              {regionsList.map((region, regionIdx) => (
                                                <Chip
                                                  key={regionIdx}
                                                  icon={<RegionIcon fontSize="small" />}
                                                  label={region}
                                                  size="small"
                                                  sx={{
                                                    backgroundColor: alpha(getColor(idx + regionIdx + 1), 0.1),
                                                    color: getColor(idx + regionIdx + 1),
                                                    fontSize: '0.65rem',
                                                    height: '18px',
                                                    '& .MuiChip-icon': {
                                                      fontSize: '0.7rem',
                                                    },
                                                    '& .MuiChip-label': {
                                                      px: 0.5,
                                                    },
                                                  }}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    const params = new URLSearchParams();
                                                    params.set('region', region);
                                                    if (fromDate) params.set('from_date', fromDate);
                                                    if (toDate) params.set('to_date', toDate);
                                                    navigate(`/products?${params.toString()}`);
                                                  }}
                                                />
                                              ))}
                                            </Box>
                                          )}
                                          
                                          {regionsList.length === 0 && (
                                            <Typography 
                                              variant="caption" 
                                              color="text.secondary"
                                              sx={{ fontStyle: 'italic' }}
                                            >
                                              Global availability
                                            </Typography>
                                          )}
                                        </Box>
                                      </Grid>
                                    );
                                  })}
                                  {product.clients.length === 0 && (
                                    <Box sx={{ textAlign: 'center', p: 2, width: '100%' }}>
                                      <InfoIcon sx={{ color: alpha(theme.palette.text.secondary, 0.5), fontSize: 36, mb: 1 }} />
                                      <Typography variant="body2" color="text.secondary">
                                        No clients found for this product.
                                      </Typography>
                                    </Box>
                                  )}
                                </Grid>
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
              
              {filteredProducts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Box sx={{ textAlign: 'center', p: 3 }}>
                      <InfoIcon sx={{ color: alpha(theme.palette.text.secondary, 0.5), fontSize: 48, mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No products found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Try adjusting your search or filters
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
}